/* Site video registry — the single source of truth for every SOP Step-7 video.
 * One entry feeds: the on-page <VideoBlock>, the VideoObject schema, and
 * sitemap-video.xml. Add an entry when a video is produced + hosted; drop a
 * <VideoBlock slug="…"/> on the relevant page.
 *
 * Hosting is flexible — give ONE of: muxPlaybackId, youtubeId, or mp4(+webm).
 * Always give a poster/thumbnail, an uploadDate (ISO), and the transcript
 * (the transcript is indexable content AND the AEO citation surface). */

export type SiteVideo = {
  /** Stable id used by <VideoBlock slug="…"/> and the sitemap. */
  slug: string;
  /** The page the video lives on (for the video sitemap <loc>). */
  page: string;
  name: string;
  description: string;
  /** Plain ISO date, e.g. "2026-06-28". Stamp it when you add the entry.
   * Widened to a full timestamp by uploadDateISO() before it reaches schema. */
  uploadDate: string;
  /** ISO-8601 duration, e.g. "PT1M25S". */
  duration?: string;
  /** Seconds — used by the video sitemap (<video:duration>). */
  durationSeconds?: number;
  /** Poster / thumbnail (absolute or site-relative). */
  thumbnailUrl: string;
  /** Provide ONE hosting option: */
  muxPlaybackId?: string;
  youtubeId?: string;
  mp4?: string;
  webm?: string;
  /** Optional .vtt captions for direct mp4/webm. */
  captionsVtt?: string;
  /** Plain-text transcript rendered on-page + in VideoObject. Optional —
   * omit until a real transcript exists (we never fabricate spoken words). */
  transcript?: string;
  /** Muted autoplay loop (hero/marketplace cuts). */
  loop?: boolean;
  /** SOP §0 locked type: SELL (explainer) · FIND (AEO) · USE (tutorial). */
  type?: "SELL" | "FIND" | "USE";
  /** Chapters → Google "key moments" (hasPart/Clip). Seconds + label. */
  chapters?: { start: number; name: string }[];
  /** Department for the /tutorials hub grouping (e.g. "Wall Art", "Stickers"). */
  department?: string;
};

/* No videos wired yet. When you host one, add it here, e.g.:
 *
 * {
 *   slug: "notion-explainer",
 *   page: "/notion",
 *   name: "Apex Notion Systems — comes set up, duplicate in one click",
 *   description: "751 Notion systems across 8 blueprints, pre-populated with sub-pages, sample rows, and three workflows. Duplicate into your workspace and you own it.",
 *   uploadDate: "2026-06-28",
 *   duration: "PT1M28S",
 *   durationSeconds: 88,
 *   thumbnailUrl: "https://…/notion-explainer-poster.jpg",
 *   muxPlaybackId: "…",            // or youtubeId / mp4(+webm)
 *   transcript: "You've bought Notion templates before…",
 * },
 */
export const SITE_VIDEOS: SiteVideo[] = [
  /* Homepage brand explainer — the 60-second "what is Apex Flow Digital" cut. */
  {
    slug: "homepage-intro",
    page: "/",
    name: "Apex Flow Digital — what it is in 60 seconds",
    description:
      "A quick tour of Apex Flow Digital: 100 done-for-you businesses, Notion systems, Canva packs, wall art, 3D models, stickers and AI staff — buy once, own it, use it today.",
    uploadDate: "2026-07-02",
    thumbnailUrl: "https://i.ytimg.com/vi/ow7QljyHEKE/hqdefault.jpg",
    youtubeId: "ow7QljyHEKE",
    type: "SELL",
    department: "Overview",
  },
  /* Universal "how to download your files" walkthrough — shown on the
   * homepage AND on every product detail page (via ProductVideoSection). */
  {
    slug: "download-guide",
    page: "/",
    name: "How to download your files",
    description:
      "Step-by-step: after you buy, where your files land, how to open your download link, and how to get your Canva / Notion / STL / sticker files onto your device.",
    uploadDate: "2026-07-02",
    thumbnailUrl: "https://i.ytimg.com/vi/lBbw-XXQSDQ/hqdefault.jpg",
    youtubeId: "lBbw-XXQSDQ",
    type: "USE",
    department: "Getting started",
  },
  /* AI Staff — how the ready-made ChatGPT assistants work. */
  {
    slug: "ai-staff",
    page: "/ai-staff",
    name: "AI Staff — set up your assistant in ChatGPT",
    description:
      "How the Apex AI Staff packs work: drop a ready-made assistant into your own ChatGPT, point it at your business, and put it to work — sales follow-up, reviews, content and more.",
    uploadDate: "2026-07-02",
    thumbnailUrl: "https://i.ytimg.com/vi/S_bSS3jQCtU/hqdefault.jpg",
    youtubeId: "S_bSS3jQCtU",
    type: "USE",
    department: "AI Staff",
  },
  /* Canva guide — lights up /templates AND every Canva pack PDP, which
   * already render <ProductVideoSection videoSlug="templates-how-to" />. */
  {
    slug: "templates-how-to",
    page: "/templates",
    name: "How to brand your Canva template",
    description:
      "Open your Apex Canva template, swap the logo, colours and text, and export — a full walkthrough of editing and branding a pack in Canva's free version.",
    uploadDate: "2026-07-02",
    thumbnailUrl: "https://i.ytimg.com/vi/saLzMUMrJA0/hqdefault.jpg",
    youtubeId: "saLzMUMrJA0",
    type: "USE",
    department: "Canva Templates",
  },
  /* 3D guide — lights up /3d-models AND every 3D library PDP, which
   * already render <ProductVideoSection videoSlug="3d-print-how-to" />. */
  {
    slug: "3d-print-how-to",
    page: "/3d-models",
    name: "How to print your 3D model",
    description:
      "From download to finished print: open your STL, drop it into your slicer, and print an Apex 3D model — settings, orientation and one-piece flexi tips.",
    uploadDate: "2026-07-02",
    thumbnailUrl: "https://i.ytimg.com/vi/4Do8oEYskR4/hqdefault.jpg",
    youtubeId: "4Do8oEYskR4",
    type: "USE",
    department: "3D Models",
  },
  /* Wall art — lights up /digital-art AND every wall-art PDP, which already
   * render <ProductVideoSection videoSlug="wall-art-print-frame" />. */
  {
    slug: "wall-art-print-frame",
    page: "/digital-art",
    name: "How to print & frame your wall art",
    description:
      "Open your high-res wall-art file and get it on the wall: which sizes are included, how to print at home or at a print shop, and how to frame it.",
    uploadDate: "2026-07-02",
    thumbnailUrl: "https://i.ytimg.com/vi/HOXy7G4f1lM/hqdefault.jpg",
    youtubeId: "HOXy7G4f1lM",
    type: "USE",
    department: "Wall Art",
  },
  /* Stickers — lights up /stickers AND every sticker PDP, which already
   * render <ProductVideoSection videoSlug="stickers-how-to-print" />. */
  {
    slug: "stickers-how-to-print",
    page: "/stickers",
    name: "How to print & cut your stickers",
    description:
      "Turn your sticker file into real stickers: printing on sticker paper, print-and-cut with a Cricut/Silhouette, or sending it to a print shop.",
    uploadDate: "2026-07-02",
    thumbnailUrl: "https://i.ytimg.com/vi/Y9ldxzQl1r4/hqdefault.jpg",
    youtubeId: "Y9ldxzQl1r4",
    type: "USE",
    department: "Stickers",
  },
  /* Notion — the /notion department guide. */
  {
    slug: "notion-how-to",
    page: "/notion",
    name: "How to use your Notion system",
    description:
      "Duplicate an Apex Notion system into your workspace in one click, then make it yours — the sub-pages, sample data and workflows are already set up.",
    uploadDate: "2026-07-02",
    thumbnailUrl: "https://i.ytimg.com/vi/is7jF_-weSU/hqdefault.jpg",
    youtubeId: "is7jF_-weSU",
    type: "USE",
    department: "Notion",
  },
  /* Websites — the /websites department guide. */
  {
    slug: "websites-how-to",
    page: "/websites",
    name: "How Apex websites work",
    description:
      "From template to launch-ready site: what you get, how it's set up for your business, and how to get yours live.",
    uploadDate: "2026-07-02",
    thumbnailUrl: "https://i.ytimg.com/vi/iRUJr1c2m7E/hqdefault.jpg",
    youtubeId: "iRUJr1c2m7E",
    type: "SELL",
    department: "Websites",
  },
  /* AI audit / AI-visibility scanner explainer. */
  {
    slug: "ai-audit",
    page: "/ai-visibility",
    name: "How the AI-visibility audit works",
    description:
      "Run the free Apex AI-visibility scan on your site, read your score, and see exactly what to fix so AI assistants (ChatGPT, Google AI) can find and quote you.",
    uploadDate: "2026-07-02",
    thumbnailUrl: "https://i.ytimg.com/vi/5VjzwnB1WUc/hqdefault.jpg",
    youtubeId: "5VjzwnB1WUc",
    type: "USE",
    department: "AI Visibility",
  },
];

export function getVideo(slug: string): SiteVideo | undefined {
  return SITE_VIDEOS.find((v) => v.slug === slug);
}

/** Best public URL for VideoObject contentUrl/embedUrl + the sitemap. */
export function videoEmbedUrl(v: SiteVideo): string | undefined {
  if (v.youtubeId) return `https://www.youtube.com/embed/${v.youtubeId}`;
  if (v.muxPlaybackId) return `https://stream.mux.com/${v.muxPlaybackId}.m3u8`;
  return v.mp4 || v.webm;
}

/** Public YouTube watch URL (for chapter deep-links in the schema). */
export function videoWatchUrl(v: SiteVideo): string | undefined {
  return v.youtubeId ? `https://www.youtube.com/watch?v=${v.youtubeId}` : undefined;
}

/** Google rejects a bare "YYYY-MM-DD" uploadDate — it wants ISO-8601 carrying a
 * timezone. Registry entries stay plain dates (easier to stamp by hand), so
 * widen them here. Noon UTC rather than midnight, so the date doesn't render a
 * day early for readers west of UTC. Full timestamps pass through untouched. */
export function uploadDateISO(uploadDate: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(uploadDate) ? `${uploadDate}T12:00:00Z` : uploadDate;
}

/** Videos that live on a given page — powers the "📹 video included" badge + on-page blocks. */
export function videosForPage(page: string): SiteVideo[] {
  return SITE_VIDEOS.filter((v) => v.page === page);
}

/** Does this page have at least one video? (drives the "video tutorial included" badge) */
export function hasVideoForPage(page: string): boolean {
  return SITE_VIDEOS.some((v) => v.page === page);
}

/** All videos grouped by department — powers the /tutorials hub. Empty until videos exist. */
export function videosByDepartment(): { department: string; videos: SiteVideo[] }[] {
  const groups = new Map<string, SiteVideo[]>();
  for (const v of SITE_VIDEOS) {
    const dept = v.department ?? "Tutorials";
    const arr = groups.get(dept) ?? [];
    arr.push(v);
    groups.set(dept, arr);
  }
  return [...groups.entries()].map(([department, videos]) => ({ department, videos }));
}
